import { useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { HintedHead } from "~/components/ui/hint-tooltip";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useDebounce } from "~/hooks/useDebounce";
import { useFetcher } from "~/hooks/useFetcher";
import {
  SEARCH_ENTITY_TYPES,
  SearchEntityType,
  SearchResponse,
  searchPath,
} from "~/services/search";
import { EntityTypeCell } from "./EntityTypeCell";
import { EntityTypeCounts } from "./EntityTypeCounts";
import { EntityTypeFilter } from "./EntityTypeFilter";
import { SnippetText } from "./SnippetText";
import { entityHref, errorMessage } from "./constants";

const LIMITS = [10, 20, 50];

// `Select` items need a non-empty value, so "any time" gets one.
const ANY_TIME = "any";

const RECENCY_OPTIONS = [
  { value: ANY_TIME, label: "Any time" },
  { value: "7", label: "Past week" },
  { value: "30", label: "Past month" },
  { value: "90", label: "Past quarter" },
];

/** A dash when that leg never returned the entity. */
const RankCell = ({ rank }: { rank: number | null }) => (
  <TableCell className="text-right tabular-nums text-muted-foreground">{rank ?? "—"}</TableCell>
);

/**
 * Query the index, with each hit's per-leg ranks, which show whether fusion is
 * doing anything.
 */
export const SearchTab = () => {
  const [query, setQuery] = useState("");
  const [entityTypes, setEntityTypes] = useState<SearchEntityType[]>(SEARCH_ENTITY_TYPES);
  const [limit, setLimit] = useState(20);
  const [updatedWithinDays, setUpdatedWithinDays] = useState<number | null>(null);
  const debouncedQuery = useDebounce(query);

  // As the caller: there is no admin lens here.
  const { data, error, isLoading } = useFetcher<SearchResponse>(
    searchPath(debouncedQuery, entityTypes, limit, updatedWithinDays)
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-64 flex-1">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search projects, datasets and sessions"
            className="pl-8"
            aria-label="Search query"
          />
        </div>
        <EntityTypeFilter value={entityTypes} onChange={setEntityTypes} />
        <Select
          value={updatedWithinDays === null ? ANY_TIME : String(updatedWithinDays)}
          onValueChange={(next) => setUpdatedWithinDays(next === ANY_TIME ? null : Number(next))}
        >
          <SelectTrigger className="w-36" aria-label="Last updated">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RECENCY_OPTIONS.map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(limit)} onValueChange={(next) => setLimit(Number(next))}>
          <SelectTrigger className="w-28" aria-label="Result limit">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LIMITS.map((option) => (
              <SelectItem key={option} value={String(option)}>
                Top {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error && (
        // The server's message: a search also calls the services that decide
        // visibility, so the failure is often theirs.
        <p className="text-sm text-destructive" data-testid="search-error">
          Search failed: {errorMessage(error)}
        </p>
      )}

      {data && (
        <EntityTypeCounts
          counts={data.type_counts}
          inaccessible={data.inaccessible_types}
          onSelect={(entityType) => setEntityTypes([entityType])}
        />
      )}

      {data && (
        <p className="text-sm text-muted-foreground">
          {data.hits.length} {data.hits.length === 1 ? "hit" : "hits"} · vector leg{" "}
          {data.vector_candidates} · lexical leg {data.lexical_candidates} · {data.took_ms}
          ms
          {data.lexical_candidates === 0 && (
            <span className="ml-2 text-amber-600 dark:text-amber-500">
              lexical leg empty — ranking is the vector leg alone
            </span>
          )}
        </p>
      )}

      {data && data.hits.length > 0 && (
        <div className="rounded-lg border border-border">
          <Table aria-label="Results">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 text-right">#</TableHead>
                <TableHead className="w-28">Type</TableHead>
                <TableHead>Result</TableHead>
                <TableHead className="w-24 text-right">
                  <HintedHead
                    label="Score"
                    hint="Built from the two ranks beside it, not from either leg's own similarity. Comparable within one search and meaningless between two."
                  />
                </TableHead>
                <TableHead className="w-24 text-right">
                  <HintedHead
                    label="Vector"
                    hint="Where the embedding leg ranked this entity. A dash means that leg never returned it."
                  />
                </TableHead>
                <TableHead className="w-24 text-right">
                  <HintedHead
                    label="Lexical"
                    hint="Where the keyword leg ranked this entity. A dash means that leg never returned it."
                  />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.hits.map((hit, index) => (
                <TableRow key={`${hit.entity_type}:${hit.entity_id}`}>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {index + 1}
                  </TableCell>
                  <EntityTypeCell entityType={hit.entity_type} />
                  <TableCell className="max-w-xl">
                    <Link
                      to={entityHref(hit.entity_type, hit.entity_id)}
                      className="font-medium hover:underline"
                    >
                      {hit.title || hit.entity_id}
                    </Link>
                    {hit.snippet && (
                      // `ts_headline` over the embedded text, so emphasis marks real matches.
                      <p className="line-clamp-2 text-xs text-muted-foreground">
                        <SnippetText snippet={hit.snippet} />
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{hit.score.toFixed(4)}</TableCell>
                  <RankCell rank={hit.vector_rank} />
                  <RankCell rank={hit.lexical_rank} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {data && data.hits.length === 0 && (
        // No access and no match both give an empty list, but only one is worth
        // rephrasing.
        <p className="text-sm text-muted-foreground">
          {data.inaccessible_types.length === entityTypes.length
            ? "You aren't authorized to see anything of the types you're searching."
            : "Nothing matched. Only entities that have been indexed are searchable."}
        </p>
      )}

      {!data && !error && !isLoading && (
        <p className="text-sm text-muted-foreground">Type a query to search the index.</p>
      )}
    </div>
  );
};
